import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    noSubject: String,
    streamingLabel: String,
    emailLabelFirst: String,
    copySubject: String,
    copyBody: String,
    copyAll: String,
    btnEdit: String,
    btnSave: String,
    btnDelete: String,
    btnSend: String,
    sending: String,
    alertNoRecipient: String,
    confirmSend: String,
    confirmResend: String,
    sendError: String,
    sendNetworkError: String,
    copied: String,
    replyCancel: String,
    replySend: String,
    versionLabel: String,
    sentLabel: String,
    sentToSuffix: String,
    sentCountSuffix: String,
    replyHeaderPrefix: String,
    replyAiBtn: String,
    replyManualBtn: String,
    replySubjectPrefix: String,
    replyComposePlaceholder: String,
    replySentTemplate: String,
    fromLabel: String,
    fromPlaceholder: String,
    toLabel: String,
    toPlaceholder: String,
    subjectLabel: String,
    copyAllSubjectPrefix: String
  }

  static targets = [
    "settingsPanel", "settingsToggle", "settingsExpandBtn",
    "emailLength", "tone", "urgency",
    "situationDescription",
    "onboarding", "versions", "loading",
    "chatId", "myProfileId", "targetProfileId", "relationship"
  ]

  connect() {
    this.settingsPanelTarget.style.display = "flex"
    this._versionCount = 0
    this._streamCard = null

    // 서버 기존 메시지 복원용
    window._emailComposeRestorePreview = (text) => this._addVersionCard(text)
    window._emailComposeRestoreSendLogs = (logs) => this._restoreSendLogs(logs)
    window._emailComposeRestoreReplies = (replies) => this._restoreReplies(replies)

    // 회신 폴링 (30초 간격)
    this._replyPollInterval = setInterval(() => this._pollReplies(), 30000)

    // 드롭다운 변경 시 자동 저장 + 카드 이메일 동기화
    document.addEventListener("email-setting-changed", () => {
      this._saveSettings()
      this._syncCardEmails()
    })

    // 상황 텍스트 입력 시 자동 저장 (blur)
    if (this.hasSituationDescriptionTarget) {
      this.situationDescriptionTarget.addEventListener("blur", () => this._saveSettings())
    }

    requestAnimationFrame(() => {
      const baWrapper = document.getElementById("ba-wrapper")
      if (!baWrapper) return
      baWrapper.addEventListener("klavy-agent:user-message", () => this._handleUserMessage())
      baWrapper.addEventListener("klavy-agent:stream-chunk", (e) => this._handleStreamChunk(e))
      baWrapper.addEventListener("klavy-agent:message-complete", (e) => this._handleMessageComplete(e))

      // KlavyAgent의 buildSystemPrompt를 래핑하여 설정 정보 주입
      const baController = this.application.getControllerForElementAndIdentifier(baWrapper, "klavy-agent")
      if (baController?._core) {
        const core = baController._core
        const originalBuild = core.buildSystemPrompt.bind(core)
        core.buildSystemPrompt = () => {
          let prompt = originalBuild()
          const settings = this._buildSettingsForPrompt()
          if (settings) prompt += "\n\n" + settings
          return prompt
        }
      }
    })
  }

  disconnect() {
    if (this._replyPollInterval) clearInterval(this._replyPollInterval)
  }

  // ── 설정 패널 ──

  toggleSettings() {
    const panel = this.settingsPanelTarget
    const hidden = panel.style.display === "none"
    panel.style.display = hidden ? "flex" : "none"
    if (this.hasSettingsExpandBtnTarget) {
      this.settingsExpandBtnTarget.style.display = hidden ? "none" : "flex"
    }
  }

  setPreset(event) {
    const text = event.currentTarget.dataset.preset
    this.situationDescriptionTarget.value = text
    this._saveSettings()
  }

  setLength(event) { this._setToggle(event, "emailLength"); this._saveSettings() }
  setTone(event) { this._setToggle(event, "tone"); this._saveSettings() }
  setUrgency(event) { this._setToggle(event, "urgency"); this._saveSettings() }

  _setToggle(event, targetName) {
    const btn = event.currentTarget
    const group = btn.closest(".email__toggle-group")
    group.querySelectorAll(".email__toggle-btn").forEach(b => b.classList.remove("active"))
    btn.classList.add("active")
    const target = this[`${targetName}Target`]
    if (target) target.value = btn.dataset.value
  }

  // ── 시나리오 버튼 → KlavyAgent 전송 ──

  sendScenario(event) {
    const text = event.currentTarget.dataset.scenario
    if (!text) return
    this._sendToKlavyAgent(text)
  }

  _sendToKlavyAgent(text) {
    const baWrapper = document.getElementById("ba-wrapper")
    if (!baWrapper) return

    const baController = this.application.getControllerForElementAndIdentifier(baWrapper, "klavy-agent")
    if (!baController || !baController._core) return

    const core = baController._core
    if (core._input) {
      core._input.value = text
      core.send()
    }
  }

  // ── 이벤트 핸들러 ──

  _handleUserMessage() {
    if (this.hasOnboardingTarget) this.onboardingTarget.style.display = "none"
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("visible")
  }

  _handleStreamChunk(e) {
    const fullText = e.detail.fullText || ""
    const cleaned = fullText.replace(/```json[\s\S]*?```/g, "").trim()

    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("visible")

    // 스트리밍용 임시 카드 생성/업데이트
    if (!this._streamCard) {
      this._streamCard = this._createCardElement(this._versionCount + 1, true)
      // 최신 카드를 맨 위에 삽입
      if (this.hasVersionsTarget) {
        this.versionsTarget.prepend(this._streamCard)
      }
    }

    const { subject, body } = this._parseEmail(cleaned)
    this._streamCard.querySelector("[data-subject]").textContent = subject || "..."
    this._streamCard.querySelector("[data-body]").innerHTML = this._renderBody(body || cleaned)
    this._scrollToTop()
  }

  _handleMessageComplete(e) {
    const text = e.detail.text || ""
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("visible")

    // 스트리밍 카드 제거
    if (this._streamCard) {
      this._streamCard.remove()
      this._streamCard = null
    }

    this._addVersionCard(text)
    this._saveMessage(text, true)
  }

  _saveMessage(content, isAi) {
    const chatId = this.hasChatIdTarget ? this.chatIdTarget.value : ""
    if (!chatId || !content) return

    const { subject } = this._parseEmail(content)
    fetch("/email/compose", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify({
        chat_id: chatId,
        content: content,
        is_ai: isAi,
        title: subject || null
      })
    }).catch(err => console.error("메시지 저장 실패:", err))
  }

  // ── 버전 카드 ──

  _addVersionCard(text) {
    const cleaned = text.replace(/```json[\s\S]*?```/g, "").trim()
    if (!cleaned) return

    if (this.hasOnboardingTarget) this.onboardingTarget.style.display = "none"

    // 기존 카드들을 older로
    if (this.hasVersionsTarget) {
      this.versionsTarget.querySelectorAll(".email__preview-card.latest").forEach(card => {
        card.classList.remove("latest")
        card.classList.add("older")
      })
    }

    this._versionCount++
    const card = this._createCardElement(this._versionCount, false)
    const { subject, body } = this._parseEmail(cleaned)
    card.querySelector("[data-subject]").textContent = subject || (this.noSubjectValue || "(제목 없음)")
    card.querySelector("[data-body]").innerHTML = this._renderBody(body || cleaned)

    // 최신 카드를 맨 위에 삽입
    if (this.hasVersionsTarget) {
      this.versionsTarget.prepend(card)
    }
    this._scrollToTop()
  }

  _createCardElement(version, isStreaming) {
    const card = document.createElement("div")
    card.className = `email__preview-card${isStreaming ? "" : " latest"}`

    const versionLabel = (this.versionLabelValue || "버전 %{version}").replace('%{version}', version)
    const label = isStreaming ? (this.streamingLabelValue || "작성 중...") : (version === 1 ? (this.emailLabelFirstValue || "생성된 이메일") : versionLabel)

    const _copySubject = this.copySubjectValue || "제목 복사"
    const _copyBody = this.copyBodyValue || "본문 복사"
    const _copyAll = this.copyAllValue || "전체 복사"
    const _btnEdit = this.btnEditValue || "수정"
    const _btnDelete = this.btnDeleteValue || "삭제"
    const _btnSend = this.btnSendValue || "발송"

    card.innerHTML = `
      <div class="email__preview-toolbar">
        <span class="email__preview-toolbar-title">${label}</span>
        <button class="email__preview-action" data-copy="subject">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="14" height="14" x="8" y="8" rx="0"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
          ${_copySubject}
        </button>
        <button class="email__preview-action" data-copy="body">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="14" height="14" x="8" y="8" rx="0"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
          ${_copyBody}
        </button>
        <button class="email__preview-action" data-copy="all">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="8" height="4" x="8" y="2" rx="0"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>
          ${_copyAll}
        </button>
        ${isStreaming ? "" : `
        <span style="width:1px;height:16px;background:#E5E7EB;"></span>
        <button class="email__preview-action" data-role="edit">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
          ${_btnEdit}
        </button>
        <button class="email__preview-action email__preview-action--delete" data-role="delete">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
          ${_btnDelete}
        </button>
        <span style="width:1px;height:16px;background:#E5E7EB;"></span>
        <button class="email__preview-action email__preview-action--send" data-role="send">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
          ${_btnSend}
        </button>
        `}
      </div>
      ${isStreaming ? "" : `
      <div class="email__preview-addresses">
        <div class="email__preview-addr-row">
          <span class="email__preview-addr-label">${this.fromLabelValue || "보내는 사람"}</span>
          <input type="email" class="email__preview-addr-input" data-from-email value="" placeholder="${this.fromPlaceholderValue || "발신자 이메일 주소"}">
        </div>
        <div class="email__preview-addr-row">
          <span class="email__preview-addr-label">${this.toLabelValue || "받는 사람"}</span>
          <input type="email" class="email__preview-addr-input" data-to-email value="" placeholder="${this.toPlaceholderValue || "수신자 이메일 주소"}">
        </div>
      </div>
      `}
      <div class="email__preview-subject">
        <div class="email__preview-subject-label">${this.subjectLabelValue || "제목"}</div>
        <div class="email__preview-subject-text" data-subject>...</div>
      </div>
      <div class="email__preview-body" data-body></div>
    `

    // 복사 버튼 이벤트
    card.querySelectorAll("[data-copy]").forEach(btn => {
      btn.addEventListener("click", () => {
        const type = btn.dataset.copy
        const subjectEl = card.querySelector("[data-subject]")
        const bodyEl = card.querySelector("[data-body]")
        let text = ""
        if (type === "subject") text = subjectEl.textContent
        else if (type === "body") text = bodyEl.innerText
        else text = `${this.copyAllSubjectPrefixValue || "제목: "}${subjectEl.textContent}\n\n${bodyEl.innerText}`
        this._copyWithFeedback(text, btn)
      })
    })

    // 수정 버튼
    const editBtn = card.querySelector("[data-role='edit']")
    if (editBtn) {
      editBtn.addEventListener("click", () => this._editCard(card))
    }

    // 삭제 버튼
    const deleteBtn = card.querySelector("[data-role='delete']")
    if (deleteBtn) {
      deleteBtn.addEventListener("click", () => this._deleteCard(card))
    }

    // 발송 버튼
    const sendBtn = card.querySelector("[data-role='send']")
    if (sendBtn) {
      sendBtn.addEventListener("click", () => this._sendCard(card))
    }

    // 이메일 주소 자동 채우기
    const fromInput = card.querySelector("[data-from-email]")
    const toInput = card.querySelector("[data-to-email]")
    if (fromInput) {
      if (this.hasMyProfileIdTarget) fromInput.value = this.myProfileIdTarget.dataset.email || ""
      fromInput.addEventListener("input", () => { fromInput.dataset.userEdited = "true" })
    }
    if (toInput) {
      if (this.hasTargetProfileIdTarget) toInput.value = this.targetProfileIdTarget.dataset.email || ""
      toInput.addEventListener("input", () => { toInput.dataset.userEdited = "true" })
    }

    return card
  }

  _editCard(card) {
    const subjectEl = card.querySelector("[data-subject]")
    const bodyEl = card.querySelector("[data-body]")
    const isEditing = card.classList.contains("editing")

    if (isEditing) {
      // 저장 모드 → 보기 모드
      const subjectInput = card.querySelector("[data-edit-subject]")
      const bodyTextarea = card.querySelector("[data-edit-body]")

      subjectEl.textContent = subjectInput.value
      bodyEl.innerHTML = this._renderBody(bodyTextarea.value)
      subjectEl.style.display = ""
      bodyEl.style.display = ""
      subjectInput.remove()
      bodyTextarea.remove()

      card.classList.remove("editing")
      const editBtn = card.querySelector("[data-role='edit']")
      if (editBtn) editBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg> ${this.btnEditValue || "수정"}`
    } else {
      // 보기 모드 → 편집 모드
      const currentSubject = subjectEl.textContent
      const currentBody = bodyEl.innerText

      // 제목 인풋
      const subjectInput = document.createElement("input")
      subjectInput.setAttribute("data-edit-subject", "")
      subjectInput.type = "text"
      subjectInput.value = currentSubject
      subjectInput.className = "email__edit-subject"
      subjectEl.style.display = "none"
      subjectEl.parentElement.appendChild(subjectInput)
      subjectInput.focus()

      // 본문 텍스트에어리어
      const bodyTextarea = document.createElement("textarea")
      bodyTextarea.setAttribute("data-edit-body", "")
      bodyTextarea.value = currentBody
      bodyTextarea.className = "email__edit-body"
      bodyEl.style.display = "none"
      bodyEl.parentElement.insertBefore(bodyTextarea, bodyEl.nextSibling)

      // 높이 자동 조절
      bodyTextarea.style.height = "auto"
      bodyTextarea.style.height = bodyTextarea.scrollHeight + "px"
      bodyTextarea.addEventListener("input", () => {
        bodyTextarea.style.height = "auto"
        bodyTextarea.style.height = bodyTextarea.scrollHeight + "px"
      })

      card.classList.add("editing")
      const editBtn = card.querySelector("[data-role='edit']")
      if (editBtn) editBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> ${this.btnSaveValue || "저장"}`
    }
  }

  _deleteCard(card) {
    card.style.transition = "opacity 0.2s, transform 0.2s"
    card.style.opacity = "0"
    card.style.transform = "scale(0.98)"
    setTimeout(() => {
      card.remove()
      // 삭제 후 남은 카드가 있으면 첫 번째를 latest로
      if (this.hasVersionsTarget) {
        const remaining = this.versionsTarget.querySelectorAll(".email__preview-card")
        remaining.forEach(c => { c.classList.remove("latest"); c.classList.add("older") })
        if (remaining.length > 0) {
          remaining[0].classList.remove("older")
          remaining[0].classList.add("latest")
        }
      }
    }, 200)
  }

  async _sendCard(card) {
    const subjectEl = card.querySelector("[data-subject]")
    const bodyEl = card.querySelector("[data-body]")
    const subject = subjectEl?.textContent?.trim()
    const body = bodyEl?.innerText?.trim()

    if (!subject || !body) return

    const fromInput = card.querySelector("[data-from-email]")
    const toInput = card.querySelector("[data-to-email]")
    const fromEmail = fromInput?.value?.trim() || ""
    const toEmail = toInput?.value?.trim() || ""

    if (!toEmail) {
      alert(this.alertNoRecipientValue || "받는 사람 이메일 주소를 입력해주세요.")
      toInput?.focus()
      return
    }

    const senderProfileId = this.hasMyProfileIdTarget ? this.myProfileIdTarget.value : ""
    const recipientProfileId = this.hasTargetProfileIdTarget ? this.targetProfileIdTarget.value : ""
    const chatId = this.hasChatIdTarget ? this.chatIdTarget.value : ""

    const sendBtn = card.querySelector("[data-role='send']")
    const isResend = sendBtn?.dataset.resend === "true"
    const confirmMsg = isResend
      ? (this.confirmResendValue || `${toEmail} 주소로 이메일을 다시 발송할까요?`).replace('%{email}', toEmail)
      : (this.confirmSendValue || `${toEmail} 주소로 이메일을 발송할까요?`).replace('%{email}', toEmail)
    if (!confirm(confirmMsg)) return

    const originalHtml = sendBtn.innerHTML
    sendBtn.disabled = true
    sendBtn.innerHTML = this.sendingValue || "발송 중..."

    try {
      const res = await fetch(`/email/compose/${chatId}/send_email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({
          subject,
          body,
          sender_profile_id: senderProfileId,
          recipient_profile_id: recipientProfileId,
          from_email: fromEmail,
          to_email: toEmail
        })
      })
      const data = await res.json()
      if (data.ok) {
        const now = new Date()
        const timeStr = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`
        const sentCount = parseInt(sendBtn.dataset.sentCount || "0") + 1
        sendBtn.dataset.sentCount = sentCount

        // 발송 완료 상태로 고정
        const lSent = this.sentLabelValue || "발송됨"
        const lCountSuffix = this.sentCountSuffixValue || "회"
        sendBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg> ${lSent} ${timeStr}${sentCount > 1 ? " (" + sentCount + lCountSuffix + ")" : ""}`
        sendBtn.style.color = "#10B981"
        sendBtn.disabled = false

        // 발송 이력 표시 추가
        let historyEl = card.querySelector("[data-send-history]")
        if (!historyEl) {
          historyEl = document.createElement("div")
          historyEl.setAttribute("data-send-history", "")
          historyEl.className = "email__send-history"
          const addrSection = card.querySelector(".email__preview-addresses")
          if (addrSection) addrSection.after(historyEl)
        }
        const entry = document.createElement("div")
        entry.className = "email__send-history-entry"
        const lSentToSuffix = this.sentToSuffixValue || "로 발송됨 ·"
        entry.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg> ${toEmail}${lSentToSuffix} ${now.getFullYear()}.${(now.getMonth()+1).toString().padStart(2,"0")}.${now.getDate().toString().padStart(2,"0")} ${timeStr}`
        historyEl.appendChild(entry)

        // 클릭 시 재발송 가능하도록 핸들러 유지 (confirm에서 "재발송" 문구)
        sendBtn.dataset.resend = "true"
      } else {
        alert(data.error || this.sendErrorValue || "발송에 실패했습니다.")
        sendBtn.innerHTML = originalHtml
        sendBtn.disabled = false
      }
    } catch (e) {
      alert(this.sendNetworkErrorValue || "발송 중 오류가 발생했습니다.")
      sendBtn.innerHTML = originalHtml
      sendBtn.disabled = false
    }
  }

  _restoreSendLogs(logs) {
    if (!this.hasVersionsTarget || !logs.length) return
    // 가장 최신 카드(=가장 마지막에 생성된)에 이력 표시
    const cards = this.versionsTarget.querySelectorAll(".email__preview-card")
    if (!cards.length) return

    // 최신 카드에 모든 발송 이력 추가
    const latestCard = cards[0]
    logs.forEach(log => {
      let historyEl = latestCard.querySelector("[data-send-history]")
      if (!historyEl) {
        historyEl = document.createElement("div")
        historyEl.setAttribute("data-send-history", "")
        historyEl.className = "email__send-history"
        const addrSection = latestCard.querySelector(".email__preview-addresses")
        if (addrSection) addrSection.after(historyEl)
      }
      const entry = document.createElement("div")
      entry.className = "email__send-history-entry"
      const lSentToSuffix2 = this.sentToSuffixValue || "로 발송됨 ·"
      entry.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg> ${log.to_email}${lSentToSuffix2} ${log.sent_at}`
      historyEl.appendChild(entry)
    })

    // 발송 버튼 상태 업데이트
    const sendBtn = latestCard.querySelector("[data-role='send']")
    if (sendBtn && logs.length > 0) {
      const lastLog = logs[logs.length - 1]
      const timeStr = lastLog.sent_at.split(" ").pop()
      const lSent2 = this.sentLabelValue || "발송됨"
      const lCountSuffix2 = this.sentCountSuffixValue || "회"
      sendBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg> ${lSent2} ${timeStr}${logs.length > 1 ? " (" + logs.length + lCountSuffix2 + ")" : ""}`
      sendBtn.style.color = "#10B981"
      sendBtn.dataset.resend = "true"
      sendBtn.dataset.sentCount = String(logs.length)
    }
  }

  // ── 회신 카드 ──

  _restoreReplies(replies) {
    if (!replies || !replies.length) return
    replies.forEach(reply => this._addReplyCard(reply))
  }

  _addReplyCard(reply) {
    const card = document.createElement("div")
    card.classList.add("email__reply-card")
    card.dataset.replyId = reply.id
    card.dataset.sendLogId = reply.email_send_log_id

    const lReplyHeader = this.replyHeaderPrefixValue || "회신 ·"
    const lReplyAiBtn = this.replyAiBtnValue || "AI로 답장 작성"
    const lReplyManualBtn = this.replyManualBtnValue || "직접 답장"
    card.innerHTML = `
      <div class="email__reply-header">
        <span>${lReplyHeader} ${this._escapeHtml(reply.received_at || '')}</span>
      </div>
      <div class="email__reply-from">
        ${this._escapeHtml(reply.from_email || '')}
      </div>
      ${reply.subject ? `<div class="email__reply-subject">${this._escapeHtml(reply.subject)}</div>` : ''}
      <div class="email__reply-body">${this._escapeHtml(reply.body_text || '')}</div>
      <div class="email__reply-actions">
        <button class="email__reply-btn email__reply-btn--primary" data-role="ai-reply">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 4V2"/><path d="M15 16v-2"/><path d="M8 9h2"/><path d="M20 9h2"/><path d="M17.8 11.8 19 13"/><path d="M15 9h.01"/><path d="M17.8 6.2 19 5"/><path d="m3 21 9-9"/><path d="M12.2 6.2 11 5"/></svg>
          ${lReplyAiBtn}
        </button>
        <button class="email__reply-btn" data-role="manual-reply">
          ${lReplyManualBtn}
        </button>
      </div>
    `

    card.querySelector('[data-role="ai-reply"]').addEventListener("click", () => {
      this._aiReplyToCard(reply, card)
    })

    card.querySelector('[data-role="manual-reply"]').addEventListener("click", () => {
      this._manualReplyToCard(reply, card)
    })

    // 카드 삽입: versions 컨테이너의 첫 번째 preview 카드 뒤에
    const versionsContainer = this.versionsTarget
    const allCards = versionsContainer.querySelectorAll(".email__preview-card")

    if (allCards.length > 0) {
      const firstCard = allCards[0]
      firstCard.after(card)
    } else {
      versionsContainer.appendChild(card)
    }
  }

  _aiReplyToCard(reply, card) {
    const parentCard = card.previousElementSibling
    let originalSubject = ''
    let originalBody = ''
    if (parentCard && parentCard.classList.contains('email__preview-card')) {
      originalSubject = parentCard.querySelector('[data-subject]')?.textContent || ''
      originalBody = parentCard.querySelector('[data-body]')?.textContent || ''
    }

    const contextMessage = `다음은 내가 보낸 이메일과 상대방의 회신입니다. 이에 대한 답장을 작성해주세요.

[내가 보낸 이메일]
제목: ${originalSubject}
${originalBody}

[상대방 회신]
보낸 사람: ${reply.from_email}
${reply.subject ? '제목: ' + reply.subject : ''}
${reply.body_text || ''}`

    const baWrapper = document.getElementById("ba-wrapper")
    if (!baWrapper) return
    const baController = this.application.getControllerForElementAndIdentifier(baWrapper, "klavy-agent")
    if (baController?._core) {
      const core = baController._core
      const inputEl = baWrapper.querySelector('[data-klavy-agent-target="input"]')
      if (inputEl) {
        inputEl.value = contextMessage
        inputEl.dispatchEvent(new Event('input'))
        core.send()
      }
    }
  }

  _manualReplyToCard(reply, card) {
    const existing = card.querySelector('.email__reply-compose')
    if (existing) { existing.remove(); return }

    const compose = document.createElement('div')
    compose.classList.add('email__reply-compose')
    compose.style.cssText = 'margin-top: 12px; padding-top: 12px; border-top: 1px solid #E5E7EB;'
    const lReplyComposePlaceholder = this.replyComposePlaceholderValue || "답장 내용을 입력하세요..."
    compose.innerHTML = `
      <textarea style="width: 100%; min-height: 120px; border: 1px solid #E5E7EB; padding: 8px; font-size: 14px; resize: vertical; font-family: inherit;" placeholder="${lReplyComposePlaceholder}"></textarea>
      <div style="display: flex; gap: 8px; margin-top: 8px;">
        <button class="email__reply-btn email__reply-btn--primary" data-role="send-manual-reply">${this.replySendValue || "발송"}</button>
        <button class="email__reply-btn" data-role="cancel-reply">${this.replyCancelValue || "취소"}</button>
      </div>
    `

    compose.querySelector('[data-role="cancel-reply"]').addEventListener('click', () => compose.remove())
    compose.querySelector('[data-role="send-manual-reply"]').addEventListener('click', async () => {
      const body = compose.querySelector('textarea').value.trim()
      if (!body) return

      const sendBtn = compose.querySelector('[data-role="send-manual-reply"]')
      const originalText = sendBtn.textContent
      sendBtn.textContent = this.sendingValue || '발송 중...'
      sendBtn.disabled = true

      const chatId = this.hasChatIdTarget ? this.chatIdTarget.value : null
      if (!chatId) return

      // 원본 카드에서 발신자 이메일 가져오기
      const parentCard = card.previousElementSibling
      const fromEmail = parentCard?.querySelector('[data-from-email]')?.value || ''
      const toEmail = reply.from_email
      const subject = `Re: ${reply.subject || ''}`

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const resp = await fetch(`/email/compose/${chatId}/send_email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ from_email: fromEmail, to_email: toEmail, subject, body, chat_id: chatId })
        })
        const data = await resp.json()
        if (data.ok) {
          const lReplySentTemplate = (this.replySentTemplateValue || "답장이 %{email}로 발송되었습니다. (%{sent_at})").replace('%{email}', toEmail).replace('%{sent_at}', data.sent_at)
          compose.innerHTML = `<p style="color: #10B981; font-size: 13px; margin-top: 8px;">${lReplySentTemplate}</p>`
        } else {
          alert(data.error || this.sendErrorValue || '발송에 실패했습니다.')
          sendBtn.textContent = originalText
          sendBtn.disabled = false
        }
      } catch (e) {
        alert(this.sendNetworkErrorValue || '발송 중 오류가 발생했습니다.')
        sendBtn.textContent = originalText
        sendBtn.disabled = false
      }
    })

    card.appendChild(compose)
  }

  async _pollReplies() {
    const chatId = this.hasChatIdTarget ? this.chatIdTarget.value : null
    if (!chatId) return

    try {
      const resp = await fetch(`/email/compose/${chatId}/replies`)
      if (!resp.ok) return
      const replies = await resp.json()

      replies.forEach(reply => {
        if (!document.querySelector(`[data-reply-id="${reply.id}"]`)) {
          this._addReplyCard(reply)
        }
      })
    } catch (e) {
      // 조용히 무시
    }
  }

  _escapeHtml(str) {
    if (!str) return ''
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  _syncCardEmails() {
    if (!this.hasVersionsTarget) return
    const fromEmail = this.hasMyProfileIdTarget ? (this.myProfileIdTarget.dataset.email || "") : ""
    const toEmail = this.hasTargetProfileIdTarget ? (this.targetProfileIdTarget.dataset.email || "") : ""
    this.versionsTarget.querySelectorAll(".email__preview-card").forEach(card => {
      const fromInput = card.querySelector("[data-from-email]")
      const toInput = card.querySelector("[data-to-email]")
      if (fromInput && !fromInput.dataset.userEdited) fromInput.value = fromEmail
      if (toInput && !toInput.dataset.userEdited) toInput.value = toEmail
    })
  }

  // ── 이메일 파싱 ──

  _parseEmail(text) {
    const subjectMatch = text.match(/(?:\*{0,2})제목(?:\*{0,2})\s*[:：]\s*(.+?)(?:\n|$)/)
    let subject = subjectMatch ? subjectMatch[1].replace(/\*{2}/g, "").trim() : ""

    let body = text
    if (subjectMatch) {
      body = text.substring(text.indexOf(subjectMatch[0]) + subjectMatch[0].length)
      body = body.replace(/^[\s\n]*(?:\*{0,2})본문(?:\*{0,2})\s*[:：]\s*\n?/, "").trim()
    }

    // AI 부가 설명 제거 (--- 구분선 이후, > 인용, 참고/안내/TIP 등)
    body = body.replace(/\n---\s*\n[\s\S]*$/, "").trim()
    body = body.replace(/\n{2,}(?:>|※|💡|참고|안내|TIP|Tip|Note)[\s\S]*$/, "").trim()

    return { subject, body }
  }

  _renderBody(text) {
    if (!text) return ""
    return text
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/\n{2,}/g, "</p><p>")
      .replace(/\n/g, "<br>")
      .replace(/^(.+)/, "<p>$1")
      .replace(/(.+)$/, "$1</p>")
  }

  // ── 복사 ──

  _copyWithFeedback(text, btn) {
    navigator.clipboard.writeText(text).then(() => {
      btn.classList.add("copied")
      const original = btn.innerHTML
      const checkSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>'
      btn.innerHTML = `${checkSvg} ${this.copiedValue || "복사됨"}`
      setTimeout(() => {
        btn.innerHTML = original
        btn.classList.remove("copied")
      }, 2000)
    })
  }

  // ── 시스템 프롬프트용 설정 정보 ──

  _buildSettingsForPrompt() {
    const lines = ["## 현재 이메일 작성 설정 (반드시 이 정보를 이메일에 반영하세요)"]

    if (this.hasMyProfileIdTarget && this.myProfileIdTarget.value) {
      const el = this.myProfileIdTarget
      const name = el.dataset.name || ""
      const position = el.dataset.position || ""
      const company = el.dataset.company || ""
      const department = el.dataset.department || ""
      if (name) {
        let sender = `- 보내는 사람: ${name}`
        if (position) sender += `, 직함: ${position}`
        if (department) sender += `, 부서: ${department}`
        if (company) sender += `, 회사: ${company}`
        lines.push(sender)
        lines.push(`  → 이메일 서명에 "${name}" 이름을 사용하세요. [보내는 본 이름] 같은 플레이스홀더를 쓰지 마세요.`)
      }
    }

    if (this.hasTargetProfileIdTarget && this.targetProfileIdTarget.value) {
      const el = this.targetProfileIdTarget
      const name = el.dataset.name || ""
      const position = el.dataset.position || ""
      const company = el.dataset.company || ""
      const department = el.dataset.department || ""
      if (name) {
        let receiver = `- 받는 사람: ${name}`
        if (position) receiver += `, 직함: ${position}`
        if (department) receiver += `, 부서: ${department}`
        if (company) receiver += `, 회사: ${company}`
        lines.push(receiver)
        lines.push(`  → 인사말에 "${name}" 이름을 사용하세요. [받는 분 성함] 같은 플레이스홀더를 쓰지 마세요.`)
      }
    }

    if (this.hasRelationshipTarget && this.relationshipTarget.value)
      lines.push(`- 관계: ${this.relationshipTarget.value}`)
    if (this.hasEmailLengthTarget && this.emailLengthTarget.value)
      lines.push(`- 글 길이: ${this.emailLengthTarget.value}`)
    if (this.hasToneTarget && this.toneTarget.value)
      lines.push(`- 어조: ${this.toneTarget.value}`)
    if (this.hasUrgencyTarget && this.urgencyTarget.value)
      lines.push(`- 긴급도: ${this.urgencyTarget.value}`)
    if (this.hasSituationDescriptionTarget && this.situationDescriptionTarget.value.trim())
      lines.push(`- 상황 설명: ${this.situationDescriptionTarget.value.trim()}`)

    lines.push("")
    lines.push("중요: 위 설정에 이름이 제공된 경우, 대괄호 플레이스홀더([이름], [소속] 등)를 절대 사용하지 마세요. 실제 이름과 정보를 이메일 본문에 직접 넣으세요.")

    return lines.length > 2 ? lines.join("\n") : ""
  }

  // ── 설정 자동 저장 ──

  _saveSettings() {
    if (this._saveTimer) clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this._doSaveSettings(), 500)
  }

  _doSaveSettings() {
    const chatId = this.hasChatIdTarget ? this.chatIdTarget.value : null
    if (!chatId) return

    const data = {
      my_profile_id: this.hasMyProfileIdTarget ? this.myProfileIdTarget.value : "",
      target_profile_id: this.hasTargetProfileIdTarget ? this.targetProfileIdTarget.value : "",
      relationship: this.hasRelationshipTarget ? this.relationshipTarget.value : "",
      email_length: this.hasEmailLengthTarget ? this.emailLengthTarget.value : "보통",
      tone: this.hasToneTarget ? this.toneTarget.value : "중립",
      urgency: this.hasUrgencyTarget ? this.urgencyTarget.value : "일반",
      situation_description: this.hasSituationDescriptionTarget ? this.situationDescriptionTarget.value : ""
    }

    fetch(`/email/compose/${chatId}/settings`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify(data)
    }).catch(() => {})
  }

  _scrollToTop() {
    const scroll = this.element.querySelector(".email__messages-scroll")
    if (scroll) scroll.scrollTop = 0
  }
}
